import {
    useMutation,
    UseMutationOptions,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from "@tanstack/react-query";
import { useAccessToken, useStandardErrorHandler } from "modules/common";
import { toast } from "react-toastify";
import {
    getOuterModuleVersionDetails,
    GetOuterModuleVersionRequest,
    getOuterModuleVersions,
    OuterModuleVersion,
    OuterModuleVersionDetails,
    OuterModuleVersionResponse,
    restoreOuterModuleVersion,
} from "services";

export const useOuterModuleVersions = (
    data: GetOuterModuleVersionRequest,
    options?: UseQueryOptions<OuterModuleVersionResponse>
) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju verzija vanjskih modula");

    const { enabled, ...opts } = options ?? {};

    return useQuery({
        queryKey: ["outerModuleVersions", data],
        queryFn: () => getOuterModuleVersions(data, accessToken!),
        enabled: Boolean(accessToken) && enabled !== false,
        onError: errorHandler,
        keepPreviousData: true,
        ...opts,
    });
};

export const useOuterModuleVersionDetails = (
    id?: string,
    options?: UseQueryOptions<OuterModuleVersionDetails>
) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju verzije vanjskih modula");

    const { enabled, ...opts } = options ?? {};

    return useQuery({
        queryKey: ["outerModuleVersions", id],
        queryFn: () => getOuterModuleVersionDetails(id!, accessToken!),
        enabled: Boolean(id) && Boolean(accessToken) && enabled !== false,
        onError: errorHandler,
        ...opts,
    });
};

export const useRestoreOuterModuleVersion = (
    options?: UseMutationOptions<OuterModuleVersion, unknown, string>
) => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler("Greška pri vraćanju verzije vanjskog modula");

    return useMutation({
        mutationFn: (id: string) => restoreOuterModuleVersion(id, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["outerModules"]);
            queryClient.invalidateQueries(["outerModuleVersions"]);
            toast.success("Verzija vanjskog modula je uspješno vraćena");
        },
        ...options,
    });
};
